'use client';

import {
  cloneElement,
  isValidElement,
  FC,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';

// Prismic rich text occasionally carries stray non-breaking spaces (from
// pasted CMS content) that block the browser from wrapping at that word
// boundary, forcing an ugly mid-word break instead once the line is too
// narrow — most visible on this slice's short, large-type headline.
// prismic-react wraps each text span in its own element even without
// formatting, so this has to walk into `props.children` rather than just
// matching top-level strings.
const stripNbsp = (node: ReactNode): ReactNode => {
  if (typeof node === 'string') {
    return node.replace(/\u00A0/g, ' ');
  }
  if (Array.isArray(node)) {
    return node.map(stripNbsp);
  }
  if (isValidElement<{ children?: ReactNode }>(node) && node.props.children) {
    return cloneElement(node, undefined, stripNbsp(node.props.children));
  }
  return node;
};

/**
 * Props for `CenteredCta`.
 */
export type CenteredCtaProps = SliceComponentProps<Content.CenteredCtaSlice>;

/**
 * Component for "CenteredCta" Slices.
 *
 * Centered text section with:
 * - Large headline
 * - Description paragraphs
 * - Glassmorphic CTA button that scrolls to form
 */
const CenteredCta: FC<CenteredCtaProps> = ({ slice }) => {
  // `slice.primary` is a union across variations; only `default` and
  // `aiRoiEbook` carry all three fields (`twoButtons` omits `button`,
  // `velxBanner` omits all). Each branch below guards with `isFilled`, so we
  // narrow to the default primary shape to read the shared fields safely.
  const { title, description, button } =
    slice.primary as Content.CenteredCtaSliceDefaultPrimary;

  const sectionRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isAiRoiEbook = slice.variation === 'aiRoiEbook';

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const elementsToAnimate = [
      { ref: headlineRef, delay: '0ms' },
      { ref: descriptionRef, delay: '150ms' },
      { ref: buttonRef, delay: '300ms' },
    ];

    elementsToAnimate.forEach(({ ref, delay }) => {
      if (ref.current) {
        ref.current.style.transitionDelay = delay;
        ref.current.classList.add(
          'opacity-0',
          'translate-y-8',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        observer.observe(ref.current);
      }
    });

    return () => observer.disconnect();
  }, []);

  const handleScrollToForm = () => {
    const formSection =
      document.querySelector('[data-slice-type="cta_with_side_form"]') ||
      document.querySelector('[data-slice-type="lead-magnet-ai-ebook"]');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const buttonLabel =
    isFilled.link(button) && button.text
      ? button.text
      : 'Download the ebook and quantify your ROI';

  if ((slice.variation as string) === 'twoButtons') {
    const twoBtnPrimary = slice.primary as unknown as {
      secondary_cta?: import('@prismicio/client').LinkField;
      primary_cta?: import('@prismicio/client').LinkField;
    };
    const { secondary_cta, primary_cta } = twoBtnPrimary;
    const swapCtas = !!(slice.primary as { swap_ctas?: boolean }).swap_ctas;

    return (
      <section
        ref={sectionRef}
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="w-full bg-[#FAFAFA] px-5 py-20 md:px-[30px] md:py-24"
      >
        <div className="mx-auto flex max-w-[1140px] flex-col items-center gap-6 text-center">
          {isFilled.richText(title) && (
            <div ref={headlineRef} className="w-full">
              <PrismicRichText
                field={title}
                components={{
                  heading1: ({ children }) => (
                    <h2 className="w-full text-balance break-words text-[32px] font-semibold leading-[1.2] text-[#041118] md:text-[44px]">
                      {stripNbsp(children)}
                    </h2>
                  ),
                }}
              />
            </div>
          )}

          {isFilled.richText(description) && (
            <div ref={descriptionRef} className="w-full max-w-[720px]">
              <PrismicRichText
                field={description}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-[15px] leading-[1.6] text-[#041118]/70 md:text-[16px]">
                      {children}
                    </p>
                  ),
                }}
              />
            </div>
          )}

          {(isFilled.link(secondary_cta) || isFilled.link(primary_cta)) && (
            <div className="mt-2 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              {isFilled.link(secondary_cta) && (
                <PrismicNextLink
                  field={secondary_cta}
                  className={`inline-flex items-center justify-center rounded-full border border-[#041118]/30 px-6 py-3 text-[15px] font-semibold text-[#041118] transition-colors hover:bg-black/5 ${
                    swapCtas ? 'order-2' : 'order-1'
                  }`}
                />
              )}
              {isFilled.link(primary_cta) && (
                <PrismicNextLink
                  field={primary_cta}
                  className={`inline-flex items-center justify-center rounded-full bg-[#00F557] px-6 py-3 text-[15px] font-bold text-[#002726] transition-colors hover:bg-[#00D964] ${
                    swapCtas ? 'order-1' : 'order-2'
                  }`}
                />
              )}
            </div>
          )}
        </div>
      </section>
    );
  }

  if ((slice.variation as string) === 'velxBanner') {
    const banner = slice.primary as unknown as {
      logo?: import('@prismicio/client').ImageField;
      background_image?: import('@prismicio/client').ImageField;
      text?: string;
      cta?: import('@prismicio/client').LinkField;
    };
    const { logo, background_image, text, cta } = banner;

    return (
      <section
        ref={sectionRef}
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="w-full px-5 py-12 md:px-[30px]"
      >
        <div className="relative mx-auto max-w-[1200px] overflow-hidden rounded-[24px] bg-[#0a0e13]">
          {isFilled.image(background_image) && (
            <PrismicNextImage
              field={background_image}
              fill
              sizes="100vw"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover"
              fallbackAlt=""
            />
          )}
          <div className="relative z-[1] flex flex-col items-center gap-8 p-8 text-left md:flex-row md:gap-12 md:p-12">
            {isFilled.image(logo) && (
              <PrismicNextImage
                field={logo}
                className="h-16 w-auto shrink-0 object-contain md:h-24"
                fallbackAlt=""
              />
            )}
            {isFilled.image(logo) && (text || isFilled.link(cta)) && (
              <div className="hidden w-px self-stretch bg-white/20 md:block" />
            )}
            <div className="flex flex-1 flex-col items-start gap-5">
              {text && (
                <p className="text-[16px] font-bold leading-[1.5] text-white md:text-[18px]">
                  {text}
                </p>
              )}
              {isFilled.link(cta) && (
                <PrismicNextLink
                  field={cta}
                  className="inline-flex items-center justify-center self-center rounded-full bg-[#00f557] px-7 py-3.5 text-[16px] font-bold leading-[1.5] text-[#002726] transition-opacity hover:opacity-90 md:self-start"
                />
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (isAiRoiEbook) {
    return (
      <section
        ref={sectionRef}
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="w-full px-5 md:px-[30px]"
      >
        <div className="mx-auto max-w-[1380px] rounded-[16px] bg-white px-5 py-16 md:px-[100px] md:py-[80px]">
          <div className="mx-auto flex max-w-[1180px] flex-col items-center gap-6 text-center">
            {/* Headline */}
            {isFilled.richText(title) && (
              <div ref={headlineRef}>
                <PrismicRichText
                  field={title}
                  components={{
                    heading1: ({ children }) => (
                      <h2 className="font-inter text-[32px] font-medium leading-[1.3] text-[#041118] md:text-[42px]">
                        {children}
                      </h2>
                    ),
                  }}
                />
              </div>
            )}

            {/* Description */}
            {isFilled.richText(description) && (
              <div ref={descriptionRef}>
                <PrismicRichText
                  field={description}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="font-inter text-[16px] font-normal leading-[1.5] text-[#041118] md:text-[18px]">
                        {children}
                      </p>
                    ),
                  }}
                />
              </div>
            )}

            {/* CTA Button */}
            {isFilled.link(button) && button.text && (
              <button
                ref={buttonRef}
                onClick={handleScrollToForm}
                className="rounded-full bg-[#00F557] px-6 py-3 font-manrope text-[16px] font-bold leading-[1.5] text-[#002726] transition-colors hover:bg-[#00D964]"
              >
                {buttonLabel}
              </button>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="w-full py-20 md:py-24"
      style={{ backgroundColor: '#0E1110' }}
    >
      <div className="mx-auto max-w-[1200px] px-6 flex flex-col items-center text-center">
        {/* Headline */}
        {isFilled.richText(title) && (
          <div ref={headlineRef} className="max-w-[1040px] mb-8">
            <PrismicRichText
              field={title}
              components={{
                heading1: ({ children }) => (
                  <h2 className="text-3xl md:text-4xl lg:text-[48px] font-semibold leading-[1.15] tracking-[-0.02em] text-white">
                    {children}
                  </h2>
                ),
              }}
            />
          </div>
        )}

        {/* Description */}
        {isFilled.richText(description) && (
          <div ref={descriptionRef} className="max-w-[746px] mb-10">
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-base md:text-lg leading-relaxed text-white mb-4 last:mb-0">
                    {children}
                  </p>
                ),
              }}
            />
          </div>
        )}

        {/* CTA Button */}
        <button
          ref={buttonRef}
          onClick={handleScrollToForm}
          className="bg-[rgba(234,234,234,0.3)] border-t border-[#eaeaea] rounded-full px-8 py-4 font-bold text-white text-center hover:bg-white/40 transition-colors cursor-pointer"
        >
          {buttonLabel}
        </button>
      </div>
    </section>
  );
};

export default CenteredCta;
